import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { lighten, makeStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import Checkbox from '@material-ui/core/Checkbox'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import { useTranslation } from 'react-i18next'
// import FilterListIcon from '@material-ui/icons/FilterList'
import { Visibility, Edit } from '@material-ui/icons'
import {
    dateFormatFunction,
    dateTimeFormatFunction,
    currencyFormat,
    capitalize,
} from '../../../../custom/common/components'
import './CustomTable.css'
import NoRecordsFound from '../../atoms/NoRecordsFound'
import { Button } from '@material-ui/core'
import Loaders from '../Loaders'
import { isValidHttpUrl, removeHTMLTags } from '@/product/common/components'

const useToolbarStyles = makeStyles((theme) => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },
    highlight:
        theme.palette.type === 'light'
            ? {
                  color: theme.palette.secondary.main,
                  backgroundColor: lighten(theme.palette.secondary.light, 0.85),
              }
            : {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.secondary.dark,
              },
    title: {
        flex: '1 1 100%',
    },
}))

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    container: {
        maxHeight: 440,
    },
    table: {
        minWidth: 750,
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
}))

const CustomTable = (props) => {
    const classes = useStyles()
    const [selected, setSelected] = useState([])
    const [dense, setDense] = useState(props.setDense ? props.setDense : false)
    const [tableBody, setTableBody] = useState(props.tableBody)
    const tableValues = props.tableValues
    const tableActions = props.tableActions
    const allData = props.allData
    const selectValues = props.selectValues
    const formik = props.formik
    const changeTableFunction = props.changeTableFunction
    const fromVariable = props.fromVariable
    const { t } = useTranslation()
    useEffect(() => {
        setTableBody(props.tableBody)

        if (props.searchData) {
            propsSelectData(props.searchData)
        } else {
            setSelected([])
        }

        return () => {
            setSelected([])
        }
    }, [props.tableBody])

    const EnhancedTableHead = (propsTable) => {
        // prettier-ignore
        const {
            classes,
            onSelectAllClick,
            order,
            orderBy,
            numSelected,
            rowCount,
            onRequestSort,
        } = propsTable

        const createSortHandler = (property) => (event) => {
            onRequestSort(event, property)
        }
        return (
            <TableHead>
                <TableRow>
                    {tableActions.length > 0 && (
                        <TableCell padding="checkbox">
                            <Checkbox
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={(e) => {
                                    onSelectAllClick(e)
                                }}
                                inputProps={{ 'aria-label': 'select all desserts' }}
                            />
                        </TableCell>
                    )}
                    {tableValues.map((headCell, index) => (
                        <>
                            {headCell.sort ? (
                                <TableCell
                                    key={index}
                                    align="left"
                                    padding={headCell.disablePadding ? 'none' : 'default'}
                                    className={headCell?.className === 'sticky' ? 'sticky' : ''}
                                    sortDirection={
                                        headCell.sortField
                                            ? orderBy === headCell.sortField
                                                ? order
                                                : false
                                            : orderBy === headCell.field
                                            ? order
                                            : false
                                    }
                                >
                                    <TableSortLabel
                                        active={
                                            headCell.sortField
                                                ? orderBy === headCell.sortField
                                                : orderBy === headCell.field
                                        }
                                        direction={
                                            headCell.sortField
                                                ? orderBy === headCell.sortField
                                                    ? order
                                                    : 'asc'
                                                : orderBy === headCell.field
                                                ? order
                                                : 'asc'
                                        }
                                        className={headCell?.class ? headCell.class : ''}
                                        onClick={createSortHandler(
                                            headCell.sortField
                                                ? headCell.sortField
                                                : headCell.field,
                                        )}
                                    >
                                        {headCell.label}
                                        {headCell.helperInfo ? (
                                            <Tooltip title={headCell.helperInfo}>
                                                <Button id="info">
                                                    <span className="material-icons">info</span>
                                                </Button>
                                            </Tooltip>
                                        ) : null}
                                        {orderBy === headCell.field ? (
                                            <span className={classes.visuallyHidden}>
                                                {order === 'desc'
                                                    ? 'sorted descending'
                                                    : 'sorted ascending'}
                                            </span>
                                        ) : null}
                                    </TableSortLabel>
                                </TableCell>
                            ) : (
                                <TableCell
                                    key={index}
                                    align="left"
                                    padding={headCell.disablePadding ? 'none' : 'default'}
                                    className={headCell?.className === 'sticky' ? 'sticky' : ''}
                                >
                                    {headCell.label}
                                </TableCell>
                            )}
                        </>
                    ))}
                </TableRow>
            </TableHead>
        )
    }

    EnhancedTableHead.propTypes = {
        classes: PropTypes.object.isRequired,
        numSelected: PropTypes.number.isRequired,
        onRequestSort: PropTypes.func.isRequired,
        onSelectAllClick: PropTypes.func.isRequired,
        order: PropTypes.oneOf(['asc', 'desc']).isRequired,
        orderBy: PropTypes.string.isRequired,
        rowCount: PropTypes.number.isRequired,
    }

    const EnhancedTableToolbar = (propsToolBar) => {
        const classes = useToolbarStyles()
        const { numSelected } = propsToolBar
        const { t } = useTranslation()
        return (
            <Toolbar
                className={clsx(
                    classes.root,
                    'tableToolbar d-flex justify-content-between align-items-center',
                    {
                        [classes.highlight]: numSelected > 0,
                    },
                )}
            >
                {numSelected > 0 ? (
                    <>
                        <Typography
                            className={classes.title}
                            color="inherit"
                            variant="subtitle1"
                            component="div"
                        >
                            {numSelected} {t('Selected')}
                        </Typography>

                        {tableActions.map((data, indexhead) => {
                            return (
                                <>
                                    <Tooltip title={t(data.label)}>
                                        <div id="tableactionid">
                                            <IconButton
                                                onClick={() => {
                                                    data.onclick(selected, data.type)
                                                }}
                                                aria-label={data.label}
                                            >
                                                {data.icon}
                                            </IconButton>
                                        </div>
                                    </Tooltip>
                                </>
                            )
                        })}
                    </>
                ) : (
                    <>
                        {!props.hideCompact ? (
                            <FormControlLabel
                                label={t('Compact View')}
                                control={<Switch checked={dense} onChange={handleChangeDense} />}
                            />
                        ) : null}

                        {/* <Tooltip title="Filter list">
                            <IconButton aria-label="filter list">
                                <FilterListIcon />
                            </IconButton>
                        </Tooltip> */}
                    </>
                )}
            </Toolbar>
        )
    }

    EnhancedTableToolbar.propTypes = {
        numSelected: PropTypes.number.isRequired,
    }

    const handleRequestSort = (event, property) => {
        const isAsc = formik.orderby === property && formik.order === 'asc'
        formik.order = isAsc ? 'desc' : 'asc'
        formik.orderby = property
        formik.page = 1
        changeTableFunction(formik, fromVariable)
    }

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            if (props?.clickId) {
                const newSelecteds = tableBody.map((n) => n[props.clickId])
                setSelected(newSelecteds)
                return
            } else {
                const newSelecteds = tableBody.map((n) => n.id)
                setSelected(newSelecteds)
                return
            }
        } else {
            setSelected([])
        }
    }

    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name)
        let newSelected = []

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name)
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1))
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1))
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            )
        }

        setSelected(newSelected)
    }

    const handleChangePage = (event, newPage) => {
        formik.page = newPage + 1
        changeTableFunction(formik, fromVariable)
    }

    const handleChangeRowsPerPage = (event) => {
        formik.limit = parseInt(event.target.value, 10)
        formik.page = 1
        changeTableFunction(formik, fromVariable)
    }

    const handleChangeDense = (event) => {
        setDense(event.target.checked)
    }

    const propsSelectData = (selectData) => {
        let values = selectData
        if (values.length) {
            const newSelecteds = []
            tableBody.map((item) => {
                if (values.includes(item.id)) {
                    newSelecteds.push(item.id)
                }
            })

            setSelected(newSelecteds)
        } else {
            setSelected([])
        }
    }

    useEffect(() => {
        if (props.searchData) {
            propsSelectData(props.searchData)
        }
    }, [props.searchData])

    useEffect(() => {
        if (props.selectedUpdate) {
            props.selectedUpdate(selected)
        }
    }, [selected])

    const isSelected = (name) => selected.indexOf(name) !== -1

    var evaluate = (c) => Function(`return ${c}`)()

    const conditionalFormattingButton = (row, valhead) => {
        let returnValue = true
        if (valhead.condition) {
            if (valhead.conditionValue) {
                let condition = valhead.conditionValue
                const isString = valhead?.conditionValue?.formula?.isString
                if (
                    evaluate(
                        isString
                            ? `"${row[condition.formula.key1]}" ${condition.formula.notation} "${
                                  condition.formula.key2
                              }"`
                            : `${row[condition.formula.key1]} ${condition.formula.notation} ${
                                  condition.formula.key2
                              }`,
                    )
                ) {
                    returnValue = true
                } else {
                    returnValue = false
                }
            }
        }
        return returnValue
    }

    const conditionalFormatting = (row, valhead) => {
        let returnValue = t(row[valhead.field]) || '-'
        if (valhead.condition) {
            if (valhead.conditionValue) {
                let condition = valhead.conditionValue
                if (
                    evaluate(
                        `${row[condition.formula.key1]} ${condition.formula.notation} ${
                            condition.formula.key2
                        }`,
                    )
                ) {
                    if (condition.true.type === 'text') {
                        returnValue = condition.true.value
                    } else if (condition.true.type === 'button') {
                        returnValue = condition.true.value
                    } else if (condition.true.type === 'key') {
                        returnValue = row[condition.true.value]
                    }
                } else {
                    if (condition.false.type === 'text') {
                        returnValue = condition.false.value
                    } else if (condition.false.type === 'button') {
                        returnValue = condition.false.value
                    } else if (condition.false.type === 'key') {
                        returnValue = row[condition.false.value]
                    }
                }
            }
            if (valhead.compressString) {
                let temp = row[valhead.field].split(' ')
                if (temp.length) {
                    if (temp[0].length < 8) {
                        returnValue = temp[0] + '...'
                    } else {
                        let string = temp[0]
                        returnValue = string.substring(0, 8) + '...'
                    }
                }
            }
        }
        return returnValue
    }

    return (
        <div className={classes.root}>
            {props.isLoading ? (
                <div className="noGridLoader">
                    <Loaders
                        name={`${props.loadingTemplate ? props.loadingTemplate : 'table'}`}
                        isLoading={props.isLoading}
                    />
                </div>
            ) : (
                <>
                    {tableBody.length ? (
                        <>
                            <Paper className={classes.paper}>
                                <EnhancedTableToolbar numSelected={selected.length} />
                                <TableContainer className={classes.container}>
                                    <Table
                                        stickyHeader
                                        className={classes.table}
                                        aria-labelledby="tableTitle"
                                        size={dense ? 'small' : 'medium'}
                                        aria-label="enhanced table"
                                    >
                                        <EnhancedTableHead
                                            classes={classes}
                                            numSelected={selected.length}
                                            order={formik.order}
                                            orderBy={formik.orderby}
                                            onSelectAllClick={handleSelectAllClick}
                                            onRequestSort={handleRequestSort}
                                            rowCount={tableBody.length}
                                        />
                                        <TableBody>
                                            {tableBody.map((row, indexhead) => {
                                                const isItemSelected = isSelected(
                                                    props.clickId ? row[props.clickId] : row.id,
                                                )
                                                const labelId = `enhanced-table-checkbox-${indexhead}`

                                                return (
                                                    <TableRow
                                                        hover
                                                        role="checkbox"
                                                        aria-checked={isItemSelected}
                                                        tabIndex={-1}
                                                        key={indexhead}
                                                        selected={isItemSelected}
                                                    >
                                                        {tableActions.length > 0 && (
                                                            <TableCell padding="checkbox">
                                                                <Checkbox
                                                                    id="listingtablecheckbox"
                                                                    onClick={(event) =>
                                                                        handleClick(
                                                                            event,
                                                                            props.clickId
                                                                                ? row[props.clickId]
                                                                                : row.id,
                                                                        )
                                                                    }
                                                                    checked={isItemSelected}
                                                                    inputProps={{
                                                                        'aria-labelledby': labelId,
                                                                    }}
                                                                />
                                                            </TableCell>
                                                        )}
                                                        {tableValues.map((valhead, index) => {
                                                            return (
                                                                <>
                                                                    <TableCell
                                                                        id={index}
                                                                        className={
                                                                            valhead?.className ===
                                                                            'sticky'
                                                                                ? 'sticky'
                                                                                : `${
                                                                                      valhead.type ===
                                                                                          'datetime' &&
                                                                                      'ws_noWrap'
                                                                                  }`
                                                                        }
                                                                    >
                                                                        {valhead.type == 'image' ? (
                                                                            <img
                                                                                className={`tableimage ${
                                                                                    dense && 'small'
                                                                                }`}
                                                                                src={
                                                                                    row[
                                                                                        valhead
                                                                                            .field
                                                                                    ]
                                                                                }
                                                                                alt={row.title}
                                                                                onError={(e) =>
                                                                                    (e.target.src =
                                                                                        '/assets/images/noimage.png')
                                                                                }
                                                                            />
                                                                        ) : valhead.type ==
                                                                          'input' ? (
                                                                            <input
                                                                                value={
                                                                                    valhead.value[
                                                                                        valhead.field +
                                                                                            row[
                                                                                                valhead
                                                                                                    .clickID
                                                                                            ]
                                                                                    ] > -1
                                                                                        ? valhead
                                                                                              .value[
                                                                                              valhead.field +
                                                                                                  row[
                                                                                                      valhead
                                                                                                          .clickID
                                                                                                  ]
                                                                                          ]
                                                                                        : row[
                                                                                              valhead
                                                                                                  .field
                                                                                          ]
                                                                                }
                                                                                onKeyUp={(e) =>
                                                                                    valhead.onClick(
                                                                                        e,
                                                                                        row[
                                                                                            valhead
                                                                                                .clickID
                                                                                        ],
                                                                                    )
                                                                                }
                                                                                onChange={(e) =>
                                                                                    valhead.onChange(
                                                                                        e,
                                                                                        row[
                                                                                            valhead
                                                                                                .clickID
                                                                                        ],
                                                                                    )
                                                                                }
                                                                                name={row.title}
                                                                                disabled={
                                                                                    valhead.disabled ||
                                                                                    false
                                                                                }
                                                                            />
                                                                        ) : valhead.type ==
                                                                          'imagewithurl' ? (
                                                                            <img
                                                                                className={`tableimage ${
                                                                                    dense && 'small'
                                                                                } ${
                                                                                    valhead?.click
                                                                                        ? 'cursorDecoy cursorDecoyHyperlink'
                                                                                        : ''
                                                                                }`}
                                                                                src={
                                                                                    isValidHttpUrl(
                                                                                        row[
                                                                                            valhead
                                                                                                .field
                                                                                        ],
                                                                                    )
                                                                                        ? row[
                                                                                              valhead
                                                                                                  .field
                                                                                          ]
                                                                                        : `${
                                                                                              global.site_url
                                                                                          }/uploads/${
                                                                                              valhead.folder
                                                                                          }/${
                                                                                              row[
                                                                                                  valhead
                                                                                                      .field
                                                                                              ]
                                                                                          }`
                                                                                }
                                                                                onClick={() => {
                                                                                    valhead.click
                                                                                        ? valhead.onclick(
                                                                                              valhead.clickID
                                                                                                  ? row[
                                                                                                        valhead
                                                                                                            .clickID
                                                                                                    ]
                                                                                                  : row.id,
                                                                                              valhead.secondaryClickID
                                                                                                  ? row[
                                                                                                        valhead
                                                                                                            .secondaryClickID
                                                                                                    ]
                                                                                                  : null,
                                                                                              valhead.clickType,
                                                                                          )
                                                                                        : ''
                                                                                }}
                                                                                alt={row.title}
                                                                                onError={(e) =>
                                                                                    (e.target.src =
                                                                                        '/assets/images/noimage.png')
                                                                                }
                                                                            />
                                                                        ) : valhead.type ==
                                                                          'imagewithcompressedurl' ? (
                                                                            <img
                                                                                className={`tableimage ${
                                                                                    dense && 'small'
                                                                                }`}
                                                                                src={`${
                                                                                    global.site_url
                                                                                }/uploads/${
                                                                                    valhead.isCompress
                                                                                        ? valhead.folder +
                                                                                          '/compressed'
                                                                                        : valhead.folder
                                                                                }/${
                                                                                    row[
                                                                                        valhead
                                                                                            .field
                                                                                    ]
                                                                                }`}
                                                                                alt={row.title}
                                                                                onError={(e) =>
                                                                                    (e.target.src =
                                                                                        '/assets/images/noimage.png')
                                                                                }
                                                                            />
                                                                        ) : valhead.type ==
                                                                          'date' ? (
                                                                            dateFormatFunction(
                                                                                row[valhead.field],
                                                                                valhead?.dateFormat,
                                                                            )
                                                                        ) : valhead.type ==
                                                                          'dropdown' ? (
                                                                            row[valhead.field] !==
                                                                                null &&
                                                                            selectValues ? (
                                                                                selectValues
                                                                                    .filter(
                                                                                        (inner) =>
                                                                                            inner.id ===
                                                                                            parseInt(
                                                                                                row[
                                                                                                    valhead
                                                                                                        .field
                                                                                                ],
                                                                                            ),
                                                                                    )
                                                                                    .map(
                                                                                        (
                                                                                            filtered,
                                                                                        ) =>
                                                                                            filtered.description,
                                                                                    )
                                                                            ) : null
                                                                        ) : valhead.type ==
                                                                          'customOptionalDropdown' ? (
                                                                            row[valhead.field] !==
                                                                                null &&
                                                                            valhead?.options ? (
                                                                                valhead?.options
                                                                                    ?.filter(
                                                                                        (inner) =>
                                                                                            inner.id ===
                                                                                            parseInt(
                                                                                                row[
                                                                                                    valhead
                                                                                                        .field
                                                                                                ],
                                                                                            ),
                                                                                    )
                                                                                    .map(
                                                                                        (
                                                                                            filtered,
                                                                                        ) =>
                                                                                            filtered.description,
                                                                                    )
                                                                            ) : null
                                                                        ) : valhead.type ==
                                                                          'multi_dropdown' ? (
                                                                            row[valhead.field] !==
                                                                                null &&
                                                                            selectValues ? (
                                                                                selectValues[
                                                                                    valhead.field
                                                                                ]
                                                                                    ?.filter(
                                                                                        (inner) =>
                                                                                            inner.id ==
                                                                                            row[
                                                                                                valhead
                                                                                                    .field
                                                                                            ],
                                                                                    )
                                                                                    .map(
                                                                                        (
                                                                                            filtered,
                                                                                        ) =>
                                                                                            filtered.description,
                                                                                    )
                                                                            ) : null
                                                                        ) : valhead.type ==
                                                                          'multi_split_dropdown' ? (
                                                                            row[valhead.field] !==
                                                                                null &&
                                                                            selectValues ? (
                                                                                row[valhead.field]
                                                                                    .split(',') // Split the comma-separated string
                                                                                    .map(
                                                                                        (id) =>
                                                                                            selectValues?.find(
                                                                                                (
                                                                                                    inner,
                                                                                                ) =>
                                                                                                    inner.id ==
                                                                                                    id,
                                                                                            )
                                                                                                ?.description,
                                                                                    )
                                                                                    .filter(Boolean)
                                                                                    .join(', ')
                                                                            ) : null
                                                                        ) : valhead.type ==
                                                                          'datetime' ? (
                                                                            dateTimeFormatFunction(
                                                                                row[valhead.field],
                                                                            )
                                                                        ) : valhead.type ==
                                                                          'price' ? (
                                                                            row[valhead.field] ? (
                                                                                currencyFormat(
                                                                                    row[
                                                                                        valhead
                                                                                            .field
                                                                                    ],
                                                                                )
                                                                            ) : (
                                                                                '-'
                                                                            )
                                                                        ) : valhead.type ==
                                                                          'countryprice' ? (
                                                                            row[valhead.field] ? (
                                                                                currencyFormat(
                                                                                    row[
                                                                                        valhead
                                                                                            .field
                                                                                    ],
                                                                                    row[
                                                                                        valhead
                                                                                            .countryfield
                                                                                    ],
                                                                                )
                                                                            ) : (
                                                                                '-'
                                                                            )
                                                                        ) : valhead.type ==
                                                                          'capitalize' ? (
                                                                            capitalize(
                                                                                removeHTMLTags(
                                                                                    row[
                                                                                        valhead
                                                                                            .field
                                                                                    ],
                                                                                ),
                                                                            )
                                                                        ) : valhead.type ==
                                                                          'hyperlink' ? (
                                                                            <span
                                                                                className="hyper-link-table"
                                                                                onClick={() => {
                                                                                    valhead.onclick(
                                                                                        valhead.clickID
                                                                                            ? row[
                                                                                                  valhead
                                                                                                      .clickID
                                                                                              ]
                                                                                            : row.id,
                                                                                        valhead.secondaryClickID
                                                                                            ? row[
                                                                                                  valhead
                                                                                                      .secondaryClickID
                                                                                              ]
                                                                                            : null,
                                                                                        valhead.clickType,
                                                                                    )
                                                                                }}
                                                                            >
                                                                                {valhead?.static_text
                                                                                    ? valhead?.static_text
                                                                                    : row[
                                                                                          valhead
                                                                                              .field
                                                                                      ]}
                                                                            </span>
                                                                        ) : valhead.type ==
                                                                          'payment_method' ? (
                                                                            row.payment_method !==
                                                                            'cash' ? (
                                                                                `${row.mk_card_number} (${row.card_type}) `
                                                                            ) : (
                                                                                capitalize(
                                                                                    row.payment_method,
                                                                                )
                                                                            )
                                                                        ) : valhead.type ==
                                                                          'button' ? (
                                                                            valhead.isMultiple ? (
                                                                                <div className="d-flex justify-content-center align-items-center">
                                                                                    {valhead.isMultiple.map(
                                                                                        (
                                                                                            inner,
                                                                                            innerIndex,
                                                                                        ) => (
                                                                                            <div
                                                                                                key={
                                                                                                    innerIndex
                                                                                                }
                                                                                            >
                                                                                                {conditionalFormattingButton(
                                                                                                    row,
                                                                                                    inner,
                                                                                                ) ? (
                                                                                                    <Tooltip
                                                                                                        title={
                                                                                                            inner.tooltipTitle
                                                                                                                ? row?.auctionTooltipTitle
                                                                                                                    ? t(
                                                                                                                          row?.auctionTooltipTitle,
                                                                                                                      )
                                                                                                                    : t(
                                                                                                                          inner.tooltipTitle,
                                                                                                                      )
                                                                                                                : t(
                                                                                                                      valhead.label,
                                                                                                                  )
                                                                                                        }
                                                                                                    >
                                                                                                        <Button
                                                                                                            id="tooltip"
                                                                                                            onClick={() => {
                                                                                                                inner.onclick(
                                                                                                                    valhead.clickID &&
                                                                                                                        !inner.clickID
                                                                                                                        ? row[
                                                                                                                              valhead
                                                                                                                                  .clickID
                                                                                                                          ]
                                                                                                                        : inner.clickID
                                                                                                                        ? row[
                                                                                                                              inner
                                                                                                                                  .clickID
                                                                                                                          ]
                                                                                                                        : row.id,
                                                                                                                    valhead.secondaryClickID
                                                                                                                        ? row[
                                                                                                                              valhead
                                                                                                                                  .secondaryClickID
                                                                                                                          ]
                                                                                                                        : inner.secondaryClickID
                                                                                                                        ? row[
                                                                                                                              inner
                                                                                                                                  .secondaryClickID
                                                                                                                          ]
                                                                                                                        : null,
                                                                                                                    inner.clickType,
                                                                                                                )
                                                                                                            }}
                                                                                                        >
                                                                                                            <span
                                                                                                                className={`${
                                                                                                                    inner?.clickType ===
                                                                                                                    'makeoffer'
                                                                                                                        ? row?.className
                                                                                                                        : inner?.className
                                                                                                                } material-icons`}
                                                                                                            >
                                                                                                                {' '}
                                                                                                                {inner.buttonType
                                                                                                                    ? inner.buttonType
                                                                                                                    : 'edit'}
                                                                                                            </span>
                                                                                                        </Button>
                                                                                                    </Tooltip>
                                                                                                ) : null}
                                                                                            </div>
                                                                                        ),
                                                                                    )}
                                                                                </div>
                                                                            ) : valhead.condition ? (
                                                                                <>
                                                                                    {valhead.conditionValue &&
                                                                                    conditionalFormatting(
                                                                                        row,
                                                                                        valhead,
                                                                                    ) ? (
                                                                                        <Tooltip
                                                                                            title={
                                                                                                valhead.tooltipTitle
                                                                                                    ? t(
                                                                                                          valhead.tooltipTitle,
                                                                                                      )
                                                                                                    : t(
                                                                                                          valhead.label,
                                                                                                      )
                                                                                            }
                                                                                        >
                                                                                            <Button
                                                                                                id="toolTitle"
                                                                                                onClick={() => {
                                                                                                    valhead.onclick(
                                                                                                        valhead.clickID
                                                                                                            ? row[
                                                                                                                  valhead
                                                                                                                      .clickID
                                                                                                              ]
                                                                                                            : row.id,
                                                                                                        valhead.secondaryClickID
                                                                                                            ? row[
                                                                                                                  valhead
                                                                                                                      .secondaryClickID
                                                                                                              ]
                                                                                                            : null,
                                                                                                        valhead.clickType,
                                                                                                    )
                                                                                                }}
                                                                                            >
                                                                                                <span className="material-icons">
                                                                                                    {valhead.conditionValue
                                                                                                        ? conditionalFormatting(
                                                                                                              row,
                                                                                                              valhead,
                                                                                                          )
                                                                                                        : '_'}
                                                                                                </span>
                                                                                            </Button>
                                                                                        </Tooltip>
                                                                                    ) : null}
                                                                                </>
                                                                            ) : (
                                                                                <Tooltip
                                                                                    title={
                                                                                        valhead.tooltipTitle
                                                                                            ? t(
                                                                                                  valhead.tooltipTitle,
                                                                                              )
                                                                                            : t(
                                                                                                  valhead.label,
                                                                                              )
                                                                                    }
                                                                                >
                                                                                    <Button
                                                                                        id="toolLabel"
                                                                                        onClick={() => {
                                                                                            valhead.onclick(
                                                                                                valhead.clickID
                                                                                                    ? row[
                                                                                                          valhead
                                                                                                              .clickID
                                                                                                      ]
                                                                                                    : row.id,
                                                                                                valhead.secondaryClickID
                                                                                                    ? row[
                                                                                                          valhead
                                                                                                              .secondaryClickID
                                                                                                      ]
                                                                                                    : null,
                                                                                                valhead.clickType,
                                                                                            )
                                                                                        }}
                                                                                    >
                                                                                        <span className="material-icons">
                                                                                            {valhead.buttonType
                                                                                                ? valhead.buttonType
                                                                                                : 'edit'}
                                                                                        </span>
                                                                                    </Button>
                                                                                </Tooltip>
                                                                            )
                                                                        ) : (
                                                                            <Tooltip
                                                                                title={
                                                                                    valhead.field ===
                                                                                    'bidscount'
                                                                                        ? t(
                                                                                              valhead.label,
                                                                                          )
                                                                                        : valhead.tooltipTitle
                                                                                        ? row[
                                                                                              valhead
                                                                                                  .tooltipTitle
                                                                                          ]
                                                                                        : ''
                                                                                }
                                                                            >
                                                                                <span
                                                                                    id="listingtitleid"
                                                                                    className={`${
                                                                                        valhead?.click
                                                                                            ? 'cursorDecoy cursorDecoyHyperlink'
                                                                                            : ''
                                                                                    } `}
                                                                                    onClick={() => {
                                                                                        valhead.click
                                                                                            ? valhead.onclick(
                                                                                                  valhead.clickID
                                                                                                      ? row[
                                                                                                            valhead
                                                                                                                .clickID
                                                                                                        ]
                                                                                                      : row.id,
                                                                                                  valhead.secondaryClickID
                                                                                                      ? row[
                                                                                                            valhead
                                                                                                                .secondaryClickID
                                                                                                        ]
                                                                                                      : null,
                                                                                                  valhead.clickType,
                                                                                              )
                                                                                            : ''
                                                                                    }}
                                                                                >
                                                                                    {valhead.field?.split(
                                                                                        ',',
                                                                                    ).length > 1 ? (
                                                                                        <>
                                                                                            {valhead.field
                                                                                                .split(
                                                                                                    ',',
                                                                                                )
                                                                                                .map(
                                                                                                    (
                                                                                                        value,
                                                                                                        index,
                                                                                                    ) =>
                                                                                                        `${
                                                                                                            row[
                                                                                                                value
                                                                                                            ] ||
                                                                                                            ''
                                                                                                        } `,
                                                                                                )}
                                                                                        </>
                                                                                    ) : row[
                                                                                          valhead
                                                                                              .field
                                                                                      ] !== null ? (
                                                                                        conditionalFormatting(
                                                                                            row,
                                                                                            valhead,
                                                                                        )
                                                                                    ) : (
                                                                                        '-'
                                                                                    )}
                                                                                </span>
                                                                            </Tooltip>
                                                                        )}
                                                                    </TableCell>
                                                                </>
                                                            )
                                                        })}
                                                    </TableRow>
                                                )
                                            })}
                                            {/* {emptyRows > 0 && (
                                        <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                            <TableCell colSpan={12} />
                                        </TableRow>
                                    )} */}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    labelRowsPerPage={t('Rows per page')}
                                    rowsPerPageOptions={
                                        [5, 20, 40, 60].includes(formik.limit)
                                            ? [5, 20, 40, 60]
                                            : [formik.limit, 5, 20, 40, 60]
                                    }
                                    component="div"
                                    count={allData.totalRecords}
                                    rowsPerPage={formik.limit}
                                    page={formik.page - 1}
                                    onChangePage={handleChangePage}
                                    onChangeRowsPerPage={handleChangeRowsPerPage}
                                />
                            </Paper>
                        </>
                    ) : (
                        <NoRecordsFound
                            noRecordPlaceholder={
                                props?.noRecordPlaceholder ? props.noRecordPlaceholder : ''
                            }
                        />
                    )}
                </>
            )}
        </div>
    )
}

export default CustomTable
